import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";

// Create context
const SearchSortContext = createContext();

// Provider component
export const SearchSortProvider = ({
  children,
  initialSearchText = "",
  initialSortBy = "name",
  initialSortOrder = "asc",
  initialPage = 0,
  initialLimit = 10,
}) => {
  const [searchText, setSearchText] = useState(initialSearchText);
  const [debouncedSearchText, setDebouncedSearchText] =
    useState(initialSearchText);
  const [sortBy, setSortBy] = useState(initialSortBy);
  const [sortOrder, setSortOrder] = useState(initialSortOrder);
  const [currentView, setCurrentView] = useState("fileExplorer"); // 'fileExplorer' or 'recycleBin'
  const [page, setPage] = useState(initialPage); // 0-based for file explorer
  const [limit, setLimit] = useState(initialLimit);

  // Debounce search text
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText]);

  // Register a fetch function for a specific view
  const [fetchFunctions, setFetchFunctions] = useState({
    fileExplorer: null,
    recycleBin: null,
  });

  const registerFetchFunction = useCallback((viewName, fetchFunction) => {
    setFetchFunctions((prev) => ({
      ...prev,
      [viewName]: fetchFunction,
    }));
  }, []);

  // Execute the appropriate fetch function based on current view
  const executeFetch = useCallback(() => {
    const fetchFunction = fetchFunctions[currentView];
    if (fetchFunction) {
      fetchFunction({
        searchText: debouncedSearchText,
        sortBy,
        sortOrder,
        page,
        limit,
      });
    }
  }, [
    fetchFunctions,
    currentView,
    debouncedSearchText,
    sortBy,
    sortOrder,
    page,
    limit,
  ]);

  // Update search text and trigger fetch
  const handleSearchChange = useCallback((value) => {
    setSearchText(value);
  }, []);

  // Update sort by and trigger fetch
  const handleSortByChange = useCallback((value) => {
    setSortBy(value);
  }, []);

  // Update sort order and trigger fetch
  const handleSortOrderChange = useCallback((value) => {
    setSortOrder(value);
  }, []);

  // Switch between views
  const switchView = useCallback((viewName) => {
    setCurrentView(viewName);
  }, []);

  const value = {
    searchText,
    debouncedSearchText,
    setSearchText: handleSearchChange,
    sortBy,
    setSortBy: handleSortByChange,
    sortOrder,
    setSortOrder: handleSortOrderChange,
    page,
    setPage,
    limit,
    setLimit,
    currentView,
    switchView,
    registerFetchFunction,
    executeFetch,
  };

  return (
    <SearchSortContext.Provider value={value}>
      {children}
    </SearchSortContext.Provider>
  );
};

// Custom hook to use the context
export const useSearchSort = () => {
  const context = useContext(SearchSortContext);
  if (!context) {
    throw new Error("useSearchSort must be used within a SearchSortProvider");
  }
  return context;
};

export default SearchSortContext;
